/**
 * `<CopyButton>` — small icon button that copies a value to the clipboard.
 *
 * On success: fires a toast `"Copied."` (variant success) and briefly
 * swaps the icon to a check mark.  Error silently no-ops (clipboard API
 * unavailable in some embedded views).
 *
 * Spec: candidate-detail-rebuild-spec.md §2.1 / global-patterns §1.2.
 * Size: 16px icon, 24px hit target (tertiary button variant).
 */
import { useState, type JSX } from "react";
import { Copy, Check } from "lucide-react";
import { useToastStore } from "@/stores/useToastStore";

interface CopyButtonProps {
  /** Value to write to the clipboard. */
  value: string;
  /** Label appended to the success toast, e.g. "Aisha's email". */
  toastLabel?: string;
  /** aria-label for the button. Defaults to "Copy". */
  ariaLabel?: string;
}

export function CopyButton({
  value,
  toastLabel,
  ariaLabel = "Copy",
}: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const addToast = useToastStore((s) => s.addToast);

  const handleClick = (): void => {
    if (!navigator.clipboard) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        const msg = toastLabel ? `Copied ${toastLabel}.` : "Copied.";
        addToast(msg, "success");
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Silently no-op — clipboard denied (e.g. insecure context).
      });
  };

  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={handleClick}
      className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-text-tertiary transition-colors hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent-secondary)]"
      data-testid="copy-button"
    >
      {copied ? (
        <Check size={13} aria-hidden className="text-status-hired-fg" />
      ) : (
        <Copy size={13} aria-hidden />
      )}
    </button>
  );
}
